import React, { useState, lazy, Suspense, useEffect } from "react";
import {
  FiMap, FiUsers, FiBriefcase, FiTruck, FiMapPin,
  FiDollarSign, FiTag, FiCalendar, FiMenu, FiX, FiChevronRight,
  FiLogOut, FiShield, FiList, FiHome
} from "react-icons/fi";

import TravelManagement from "./TravelManagement";
import UserManagement from "./UserManagement";
import EmployeeManagement from "./EmployeeManagement";
import BusManagement from "./BusManagement";
import TerminalManagement from "./TerminalManagement";
import SalesManagement from "./SalesManagement";
import PromotionManagement from "./PromotionManagement";
import CourseManagement from "./CourseManagement";
import styles from "./AdminDashboard.module.css";
import api from "../api/api";

const ListinManagement = lazy(() => import("./ListinManagement"));

const sections = [
  { key: "travels",    label: "Rutas",      icon: <FiMap size={18} />,      description: "Gestionar rutas de viaje" },
  { key: "courses",    label: "Viajes",     icon: <FiCalendar size={18} />, description: "Gestión de viajes programados" },
  { key: "listin",     label: "Listín",     icon: <FiList size={18} />,     description: "Manifiesto y control de abordaje" },
  { key: "users",      label: "Usuarios",   icon: <FiUsers size={18} />,    description: "Administrar usuarios" },
  { key: "employee",   label: "Empleados",  icon: <FiBriefcase size={18} />,description: "Gestionar personal" },
  { key: "buses",      label: "Buses",      icon: <FiTruck size={18} />,    description: "Flota de vehículos" },
  { key: "terminals",  label: "Terminales", icon: <FiMapPin size={18} />,   description: "Puntos de operación" },
  { key: "sale",       label: "Ventas",     icon: <FiDollarSign size={18} />,description: "Control de ventas" },
  { key: "promotions", label: "Promociones",icon: <FiTag size={18} />,      description: "Ofertas y descuentos" },
];

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState(
    () => sessionStorage.getItem('admin_tab') || "travels"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Cerrar sidebar si el viewport es grande o si presionan Escape
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    const handleResize  = () => { if (window.innerWidth > 900) setSidebarOpen(false); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize',  handleResize);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize',  handleResize);
    };
  }, []);

  const handleSectionClick = (key) => {
    setActiveSection(key);
    sessionStorage.setItem('admin_tab', key);
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch {
      // Ignorar errores de red
    }
    localStorage.removeItem('currentUser');
    window.location.href = '/';
  };

  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const userName = currentUser.user?.name || 'Administrador';
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <div className={styles.layout}>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        {/* Brand */}
        <div className={styles.brand}>
          <div className={styles.brandLogo}>
            <FiShield size={22} />
          </div>
          <div className={styles.brandInfo}>
            <span className={styles.brandName}>Flamingo</span>
            <span className={styles.brandRole}>Administración</span>
          </div>
          <button className={styles.closeSidebar} onClick={() => setSidebarOpen(false)}>
            <FiX size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className={styles.sidebarNav}>
          <button
            className={styles.navItem}
            onClick={() => { window.location.href = '/'; }}
          >
            <span className={styles.navIcon}><FiHome size={18} /></span>
            <span className={styles.navLabel}>Inicio</span>
          </button>
          <span className={styles.navSection}>MENÚ PRINCIPAL</span>
          {sections.map((s) => (
            <button
              key={s.key}
              className={`${styles.navItem} ${activeSection === s.key ? styles.navItemActive : ''}`}
              onClick={() => handleSectionClick(s.key)}
            >
              <span className={styles.navIcon}>{s.icon}</span>
              <span className={styles.navLabel}>{s.label}</span>
              {activeSection === s.key && (
                <FiChevronRight size={14} className={styles.navArrow} />
              )}
            </button>
          ))}
        </nav>

        {/* User section at bottom */}
        <div className={styles.sidebarFooter}>
          <div className={styles.userCard}>
            <div className={styles.userAvatar}>{userInitial}</div>
            <div className={styles.userInfo}>
              <span className={styles.userName}>{userName}</span>
              <span className={styles.userRole}>Admin</span>
            </div>
          </div>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            <FiLogOut size={16} />
            <span>Salir</span>
          </button>
        </div>
      </aside>

      <main className={styles.mainContent}>
        <header className={styles.topBar}>
          <button className={styles.menuToggle} onClick={() => setSidebarOpen(true)}>
            <FiMenu size={22} />
          </button>
          <div className={styles.topBarContent}>
            <h1 className={styles.pageTitle}>
              {sections.find(s => s.key === activeSection)?.label || 'Dashboard'}
            </h1>
            <p className={styles.pageDescription}>
              {sections.find(s => s.key === activeSection)?.description || ''}
            </p>
          </div>
        </header>

        <div className={styles.contentArea}>
          {activeSection === "travels"    && <TravelManagement />}
          {activeSection === "courses"    && <CourseManagement />}
          {activeSection === "listin"     && <Suspense fallback={<p style={{padding:'24px',color:'#666'}}>Cargando listín...</p>}><ListinManagement /></Suspense>}
          {activeSection === "users"      && <UserManagement />}
          {activeSection === "employee"   && <EmployeeManagement />}
          {activeSection === "buses"      && <BusManagement />}
          {activeSection === "terminals"  && <TerminalManagement />}
          {activeSection === "sale"       && <SalesManagement />}
          {activeSection === "promotions" && <PromotionManagement />}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
